import React, { ReactNode } from "react";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Paper,
  Typography,
} from "@mui/material";
import "../../assets/styles/common/table.css";

export interface ColumnDefinition<T> {
  id: string;
  label: string;
  align?: "left" | "right" | "center";
  minWidth?: number;
  format?: (row: T) => ReactNode;
  sortable?: boolean;
}

export type SortDirection = "asc" | "desc";

export interface SortState {
  columnId: string;
  direction: SortDirection;
}

interface GenericTableProps<T> {
  columns: ColumnDefinition<T>[];
  data: T[];
  getRowKey: (row: T) => string | number;
  loading?: boolean;
  emptyMessage?: string;
  onRowClick?: (row: T) => void;
  /** Highlights the row whose key matches (used for master/detail selection). */
  selectedRowKey?: string | number | null;
  /** Renders a full-width detail row directly beneath the selected row. */
  renderDetailRow?: (row: T) => ReactNode;
  actionsColumn?: {
    label: string;
    align?: "left" | "right" | "center";
    render: (row: T) => ReactNode;
  };
  showResultsCount?: boolean;
  /**
   * Current sort column/direction. When provided together with `onSortChange`, columns marked
   * `sortable` render a clickable `TableSortLabel` header. Omit both for existing callers that
   * don't support server-driven sorting — fully backward compatible (no-op).
   */
  sortState?: SortState | null;
  onSortChange?: (columnId: string) => void;
}

function GenericTable<T>({
  columns,
  data,
  getRowKey,
  loading = false,
  emptyMessage = "No data available",
  onRowClick,
  selectedRowKey = null,
  renderDetailRow,
  actionsColumn,
  showResultsCount = true,
  sortState = null,
  onSortChange,
}: GenericTableProps<T>) {
  const columnCount = columns.length + (actionsColumn ? 1 : 0);

  return (
    <Box>
      <TableContainer
        component={Paper}
        className="generic-table-container"
      >
        <Table>
          <TableHead>
            <TableRow>
              {columns.map((column) => {
                const isSortable = Boolean(column.sortable && onSortChange);
                const isActiveSortColumn = sortState?.columnId === column.id;

                return (
                  <TableCell
                    key={column.id}
                    align={column.align || "left"}
                    className="generic-table-header-cell"
                    sx={{ minWidth: column.minWidth }}
                  >
                    {isSortable ? (
                      <TableSortLabel
                        active={isActiveSortColumn}
                        direction={isActiveSortColumn ? sortState!.direction : "asc"}
                        onClick={() => onSortChange!(column.id)}
                      >
                        {column.label}
                      </TableSortLabel>
                    ) : (
                      column.label
                    )}
                  </TableCell>
                );
              })}
              {actionsColumn && (
                <TableCell
                  align={actionsColumn.align || "right"}
                  className="generic-table-header-cell"
                >
                  {actionsColumn.label}
                </TableCell>
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell
                  colSpan={columns.length + (actionsColumn ? 1 : 0)}
                  align="center"
                  className="generic-table-body-cell"
                  sx={{ py: 4 }}
                >
                  Loading...
                </TableCell>
              </TableRow>
            ) : data.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={columns.length + (actionsColumn ? 1 : 0)}
                  align="center"
                  className="generic-table-body-cell"
                  sx={{ py: 4 }}
                >
                  {emptyMessage}
                </TableCell>
              </TableRow>
            ) : (
              data.map((row) => {
                const rowKey = getRowKey(row);
                const isSelected =
                  selectedRowKey !== null &&
                  selectedRowKey !== undefined &&
                  rowKey === selectedRowKey;

                const showDetail = isSelected && renderDetailRow;

                return (
                  <React.Fragment key={rowKey}>
                    <TableRow
                      onClick={() => onRowClick?.(row)}
                      className={`generic-table-row ${onRowClick ? "generic-table-row-clickable" : ""}`}
                      sx={
                        isSelected
                          ? {
                              backgroundColor: "hsl(19 101% 51% / 0.12) !important",
                              "& .generic-table-body-cell": {
                                borderBottomColor: showDetail
                                  ? "transparent"
                                  : "hsl(19 101% 51% / 0.35)",
                              },
                            }
                          : undefined
                      }
                    >
                      {columns.map((column) => (
                        <TableCell
                          key={column.id}
                          align={column.align || "left"}
                          className="generic-table-body-cell"
                        >
                          {column.format
                            ? column.format(row)
                            : (row as Record<string, ReactNode>)[column.id]}
                        </TableCell>
                      ))}
                      {actionsColumn && (
                        <TableCell align={actionsColumn.align || "right"}>
                          {actionsColumn.render(row)}
                        </TableCell>
                      )}
                    </TableRow>
                    {showDetail && (
                      <TableRow className="generic-table-detail-row">
                        <TableCell
                          colSpan={columnCount}
                          className="generic-table-detail-cell"
                          sx={{ p: 0, borderBottom: "1px solid #333" }}
                        >
                          {renderDetailRow(row)}
                        </TableCell>
                      </TableRow>
                    )}
                  </React.Fragment>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Results count */}
      {showResultsCount && data.length > 0 && (
        <Box className="generic-table-results-count">
          <Typography variant="body2">
            Showing {data.length} {data.length === 1 ? "result" : "results"}
          </Typography>
        </Box>
      )}
    </Box>
  );
}

export default GenericTable;
